import { DateTime } from 'luxon';
import { use, useEffect, useState } from 'react';
import { Button, Form, Spinner } from 'react-bootstrap';
import { useTranslation } from "react-i18next";

import useAlert from '../../Alerts/useAlert';
import { UserPreferencesContext } from '../../Contexts/UserPreferencesContext';
import DateRange from '../../DateRange';
import { BusinessUnit } from '../../Types/BusinessUnit';
import { useBusinessUnits } from '../../Utilities/useBusinessUnits';
import { useHttpUtils } from '../../Utilities/useHttpUtils';
import HTMLReportArea from '../Reporting/HTMLReportArea';

export default function PackagingReport() {
    const [loadingReport, setLoadingReport] = useState<boolean>(false);
    const [report, setReport] = useState<string>();
    const { fetchBlobAuthenticated } = useHttpUtils();
    const { setAlert } = useAlert();
    const { t } = useTranslation();

    const getFromDate = () => {
        const yesterdayStart = DateTime.now().minus({ day: 1 }).startOf('day');
        // Using toISO creates a string which includes the correct offset so any JS Date created from this string also mantains that offset
        // This is in constrast to typically making JSDates which uses the browsers time zone to create and assign offsets
        return yesterdayStart.toISO();
    };

    const getToDate = () => {
        const yesterdayStart = DateTime.now().minus({ day: 1 }).startOf('day');
        return yesterdayStart.toISO();
    };

    const defaultDateRange = {
        from: getFromDate(),
        to: getToDate()
    };

    const { businessUnits } = useBusinessUnits();
    const { userPreferences } = use(UserPreferencesContext);
    const [packageBusinessUnits, setPackageBusinessUnits] = useState<BusinessUnit[]>([]);
    const [selectedBusinessUnit, setSelectedBusinessUnit] = useState<BusinessUnit>();
    const [dateFilter, setDateFilter] = useState(defaultDateRange);
    const [formValidated, setFormValidated] = useState(false);

    useEffect(() => {
        setPackageBusinessUnits(businessUnits.filter(bu => bu.businessUnitType === "Package"));
    }, [businessUnits]);

    useEffect(() => {
        setSelectedBusinessUnit(packageBusinessUnits.find(bu => userPreferences?.lotList?.defaultBusinessUnits?.find(code => code === bu.code)));
    }, [packageBusinessUnits, userPreferences?.lotList?.defaultBusinessUnits]);

    const getLastXDays = (x: number) => {
        const days: DateTime[] = [];
        for (let i = 1; i <= x; i++) {
            days.push(DateTime.now().minus({ day: i }).startOf('day'));
        }
        return days;
    };

    const ReportForTheDayButtons = ({ numberOfDays }: { numberOfDays: number }) => {
        const days = getLastXDays(numberOfDays);
        return (
            <div className="d-inline-flex me-3">
                {days.reverse().map((day, i) =>
                    <Button className="me-1" key={i} onClick={() => getPackagingReport(day)}>{day.toLocaleString(DateTime.DATE_SHORT)}</Button>
                )}
            </div>
        );
    };

    const getPackagingReport = async (date: DateTime | undefined = undefined) => {
        setLoadingReport(true);

        let from: DateTime;
        let to: DateTime;

        if (date !== undefined) {
            from = date;
            to = date;
        } else {
            from = DateTime.fromISO(dateFilter?.from);
            to = DateTime.fromISO(dateFilter?.to);
        }

        let url = `api/v1/lots/packagingreport?dateFromUTC=${from.toUTC().toISO()}&dateToUTC=${to.toUTC().toISO()}`;
        if (selectedBusinessUnit) {
            url = url + `&businessUnits=${selectedBusinessUnit.code}`;
        }

        await fetchBlobAuthenticated(url)
            .then(async response => {
                if (response.ok) {
                    return response.text();
                } else {
                    throw new Error(await response.text());
                }
            })
            .then(text => {
                setLoadingReport(false);
                setReport(text);
            })
            .catch(error => {
                setLoadingReport(false);
                setAlert("danger", "Error", error.message);
            });
    };

    const handleSubmit = (event: any) => {
        event.preventDefault();
        event.stopPropagation();

        setFormValidated(true);

        const form = event.currentTarget;

        if (form.checkValidity() === true) {
            getPackagingReport();
        }
    };

    let downloadName = `packaging-report.html`;

    return (
        <>
            <Form id="reportingPackagingForm" noValidate validated={formValidated} onSubmit={handleSubmit}>
                <div className="d-flex mb-3">
                    <div className="d-inline-flex me-3">
                        <ReportForTheDayButtons numberOfDays={3} />
                        <DateRange dateFilter={dateFilter} setDateFilter={setDateFilter} />
                    </div>
                    <div>
                        <Form.Select
                            required
                            value={selectedBusinessUnit?.code}
                            onChange={(e) => setSelectedBusinessUnit(packageBusinessUnits.find(x => x.code === e.target.value))}>
                            <option value="">{t("Application.Controls.DefaultSelect")}</option>
                            {packageBusinessUnits &&
                                packageBusinessUnits.map((bu, i) => {
                                    return <option key={`lot-status-option-${i}`} value={bu.code}>{bu.code}</option>
                                })
                            }
                        </Form.Select>
                    </div>
                    <div className="ms-auto ms-3">
                        <Button variant="primary" type="submit" form="reportingPackagingForm" disabled={loadingReport}>
                            <>
                                {t("PackagingList.DisplayData")}
                                {loadingReport &&
                                    <Spinner
                                        as="span"
                                        animation="border"
                                        size="sm"
                                        role="status"
                                        aria-hidden="true"
                                        className="ms-2"
                                    />
                                }
                            </>
                        </Button>
                    </div>
                </div>
                <HTMLReportArea loadingReport={loadingReport} report={report} downloadName={downloadName}></HTMLReportArea>
            </Form>
        </>
    );
}